import { EditNotification, GetNotification, RegisterNotification } from "@/dtos/Notification.dto";
import { StateObject } from "@/dtos/State.dto";
import Notification from "@/services/notification/notification";
import {
  CLEAR_NOTIFICATION_ISLOADING,
  CLOSE_NOTIFICATION_POPUP_ACTION,
  DELETE_NOTIFICATION,
  EDIT_NOTIFICATION,
  GET_NOTIFICATIONS,
  LOAD_MORE_NOTIFICATIONS,
  MARK_NOTIFICATION_SEEN,
  PUSH_NOTIFICATION_POPUP_ACTION,
  REGISTER_NOTIFICATION,
} from "@/store/action-types";
import {
  ADD_DISMISSED_BANNER_TIMESTAMP,
  APPEND_NOTIFICATIONS,
  CLOSE_NOTIFICATION_POPUP,
  NEXT_NOTIFICATION_POPUP,
  PUSH_NOTIFICATION_POPUP,
  SET_ERRORMSG,
  SET_NOTIFICATION,
  SET_NOTIFICATIONS,
  SET_NOTIFICATIONS_PAGINATION,
  SOCKET_BOUND,
} from "@/store/mutation-types";
import { NotificationCategory } from "@/utils/enums";
import { AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";
import { Module } from "vuex";

const notification: Module<any, {}> = {
  state: {
    notification: {
      data: null,
      isLoading: false,
    },
    notifications: {
      data: null,
      isLoading: false,
      isLoadingMore: false,
    },
    notificationsPagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      hasMore: false,
    },
    popupQueue: [],
    activePopup: null,
    socketBound: false,
    enrichmentBanner: {
      visible: false,
      contactsCount: 0,
      emailsCount: 0,
      phonesCount: 0,
      bucketName: null,
      linkUrl: null as string | null,
      contactIds: [] as string[],
      timestamp: null as number | null,
    },
    callBucketPrompt: {
      visible: false,
      callLogId: null as string | null,
      contactId: null as string | null,
      contactName: null as string | null,
      duration: null as number | null,
      message: null as string | null,
      timestamp: null as number | null,
    },
    dismissedBannerTimestamps: [] as number[],
  },
  getters: {
    notification: (state: { notification: any }) => state.notification,
    notifications: (state: { notifications: any }) => state.notifications,
    notificationsPagination: (state) => state.notificationsPagination,
    activePopup: (state) => state.activePopup,
    enrichmentBanner: (state) => state.enrichmentBanner,
    callBucketPrompt: (state) => state.callBucketPrompt,
  },
  mutations: {
    [SET_NOTIFICATION]: (
      state: { notification: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.notification.data = payload.data;
      state.notification.isLoading = payload.isLoading;
    },
    [SET_NOTIFICATIONS]: (
      state: any,
      payload: { data?: any; status?: any; isLoading?: boolean; isLoadingMore?: boolean }
    ) => {
      if (payload.data != null) {
        state.notifications.data = payload.data;
      }
      if (payload.isLoading !== undefined) {
        state.notifications.isLoading = payload.isLoading;
      }
      if (payload.isLoadingMore !== undefined) {
        state.notifications.isLoadingMore = payload.isLoadingMore;
      }
    },
    [APPEND_NOTIFICATIONS]: (state: any, payload: { data: any[]; isLoadingMore: boolean }) => {
      if (payload.data && Array.isArray(payload.data)) {
        const existingIds = new Set((state.notifications.data || []).map((n: any) => n.id));
        const newNotifications = payload.data.filter((n: any) => !existingIds.has(n.id));
        state.notifications.data = [...(state.notifications.data || []), ...newNotifications];
      }
      state.notifications.isLoadingMore = payload.isLoadingMore;
    },
    [SET_NOTIFICATIONS_PAGINATION]: (
      state: any,
      payload: { currentPage: number; totalPages: number; totalItems: number; hasMore: boolean }
    ) => {
      state.notificationsPagination = {
        currentPage: payload.currentPage,
        totalPages: payload.totalPages,
        totalItems: payload.totalItems,
        hasMore: payload.hasMore,
      };
    },

    [PUSH_NOTIFICATION_POPUP]: (
      state,
      payload: {
        title: string;
        message: string;
        type?: string;
        meta?: any;
        ts?: number;
        linkUrl?: string | null;
      }
    ) => {
      const id = uuidv4();
      const notice = {
        id,
        type: "info",
        ts: Date.now(),
        linkUrl: payload.linkUrl || null,
        ...payload,
      };
      state.popupQueue.push(notice);
      state.activePopup = notice;
    },
    [NEXT_NOTIFICATION_POPUP]: (state) => {
      state.activePopup = state.popupQueue.shift() || null;
    },
    [CLOSE_NOTIFICATION_POPUP]: (state) => {
      state.activePopup = null;
    },
    [SOCKET_BOUND]: (state) => {
      state.socketBound = true;
    },
    SET_ENRICHMENT_BANNER: (state, payload) => {
      const bannerTimestamp = payload.timestamp || Date.now();

      if (state.dismissedBannerTimestamps.includes(bannerTimestamp)) {
        return;
      }

      state.enrichmentBanner = {
        visible: payload.visible !== undefined ? payload.visible : true,
        contactsCount: payload.contactsCount || 0,
        emailsCount: payload.emailsCount || 0,
        phonesCount: payload.phonesCount || 0,
        bucketName: payload.bucketName || null,
        linkUrl: payload.linkUrl || null,
        contactIds: payload.contactIds || [],
        timestamp: bannerTimestamp,
      };
    },
    HIDE_ENRICHMENT_BANNER: (state) => {
      if (state.enrichmentBanner.timestamp) {
        state.dismissedBannerTimestamps.push(state.enrichmentBanner.timestamp);
        if (state.dismissedBannerTimestamps.length > 50) {
          state.dismissedBannerTimestamps.shift();
        }
      }

      state.enrichmentBanner.visible = false;
      state.enrichmentBanner.contactIds = [];
      state.enrichmentBanner.timestamp = null;
    },
    [ADD_DISMISSED_BANNER_TIMESTAMP]: (state, timestamp: number) => {
      if (!state.dismissedBannerTimestamps.includes(timestamp)) {
        state.dismissedBannerTimestamps.push(timestamp);
        if (state.dismissedBannerTimestamps.length > 50) {
          state.dismissedBannerTimestamps.shift();
        }
      }
    },
    SET_CALL_BUCKET_PROMPT: (state, payload) => {
      state.callBucketPrompt = {
        visible: true,
        callLogId: payload.callLogId || null,
        contactId: payload.contactId || null,
        contactName: payload.contactName || null,
        duration: payload.duration || null,
        message: payload.message || null,
        timestamp: payload.timestamp || Date.now(),
      };
    },
    HIDE_CALL_BUCKET_PROMPT: (state) => {
      state.callBucketPrompt.visible = false;
      state.callBucketPrompt.callLogId = null;
      state.callBucketPrompt.contactId = null;
      state.callBucketPrompt.contactName = null;
      state.callBucketPrompt.duration = null;
      state.callBucketPrompt.message = null;
      state.callBucketPrompt.timestamp = null;
    },
  },
  actions: {
    [REGISTER_NOTIFICATION]: async ({ commit }: any, payload: any) => {
      commit(SET_NOTIFICATION, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Notification.registerNotification(
          payload as RegisterNotification
        );

        if (result) {
          if (result.data.status) {
            commit(SET_NOTIFICATION, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_NOTIFICATION, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_NOTIFICATION, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_NOTIFICATION, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to register notification");
        }
        return false;
      }
    },
    [GET_NOTIFICATIONS]: async ({ commit, state }: any, payload: any) => {
      commit(SET_NOTIFICATIONS, { data: null, isLoading: true });

      try {
        const requestPayload = {
          ...payload,
          page: 1,
          limit: payload?.limit || 15,
        } as GetNotification;

        const result: AxiosResponse = await Notification.getNotifications(requestPayload);

        if (result) {
          if (result.data.status) {
            const pagination = result.data.metaData;
            commit(SET_NOTIFICATIONS, {
              data: result.data.data,
              isLoading: false,
            });
            if (pagination) {
              commit(SET_NOTIFICATIONS_PAGINATION, {
                currentPage: pagination.currentPage || 1,
                totalPages: pagination.totalPages || 1,
                totalItems: pagination.totalItems || 0,
                hasMore: pagination.currentPage < pagination.totalPages,
              });
            }

            return true;
          }
          commit(SET_NOTIFICATIONS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_NOTIFICATIONS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_NOTIFICATIONS, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load notifications");
        }
        return false;
      }
    },
    [LOAD_MORE_NOTIFICATIONS]: async ({ commit, state }: any, payload: any) => {
      const currentPagination = state.notificationsPagination;
      if (!currentPagination.hasMore || state.notifications.isLoadingMore) {
        return false;
      }

      commit(SET_NOTIFICATIONS, { isLoadingMore: true });

      try {
        const requestPayload = {
          ...payload,
          page: currentPagination.currentPage + 1,
          limit: payload?.limit || 15,
        } as GetNotification;

        const result: AxiosResponse = await Notification.getNotifications(requestPayload);

        if (result) {
          if (result.data.status) {
            const pagination = result.data.metaData;
            commit(APPEND_NOTIFICATIONS, {
              data: result.data.data,
              isLoadingMore: false,
            });
            if (pagination) {
              commit(SET_NOTIFICATIONS_PAGINATION, {
                currentPage: pagination.currentPage || 1,
                totalPages: pagination.totalPages || 1,
                totalItems: pagination.totalItems || 0,
                hasMore: pagination.currentPage < pagination.totalPages,
              });
            }

            return true;
          }
          commit(SET_NOTIFICATIONS, { isLoadingMore: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_NOTIFICATIONS, { isLoadingMore: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_NOTIFICATIONS, { isLoadingMore: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load more notifications"
          );
        }
        return false;
      }
    },
    [EDIT_NOTIFICATION]: async ({ commit }: any, payload: any) => {
      commit(SET_NOTIFICATION, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Notification.editNotification(
          payload as EditNotification
        );

        if (result) {
          if (result.data.status) {
            commit(SET_NOTIFICATION, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_NOTIFICATION, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_NOTIFICATION, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_NOTIFICATION, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit notification");
        }
        return false;
      }
    },
    [MARK_NOTIFICATION_SEEN]: async ({ commit }: any, payload: any) => {
      commit(SET_NOTIFICATION, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Notification.markNotificationSeen(
          payload as EditNotification
        );

        if (result) {
          if (result.data.status) {
            commit(SET_NOTIFICATION, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_NOTIFICATION, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_NOTIFICATION, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_NOTIFICATION, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to mark notification as seen"
          );
        }
        return false;
      }
    },
    [DELETE_NOTIFICATION]: async ({ commit }: any, payload: string) => {
      commit(SET_NOTIFICATION, { data: { id: payload }, isLoading: true });

      try {
        const result: AxiosResponse = await Notification.deleteNotification(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_NOTIFICATION, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_NOTIFICATION, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_NOTIFICATION, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_NOTIFICATION, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete notification");
        }
        return false;
      }
    },
    [CLEAR_NOTIFICATION_ISLOADING]: ({ commit }) => {
      commit(SET_NOTIFICATION, { isLoading: false });
      commit(SET_NOTIFICATIONS, { isLoading: false });
    },

    [PUSH_NOTIFICATION_POPUP_ACTION]: ({ commit }, payload) => {
      // Handle notifications based on category
      switch (payload.category) {
        case NotificationCategory.enrichment: {
          const d = payload.data;
          const isHubSpot = d?.importedCount !== undefined;
          const isCsv = d?.contactsCount !== undefined;
          if (isHubSpot || isCsv) {
            commit("SET_ENRICHMENT_BANNER", {
              visible: true,
              contactsCount: isHubSpot ? d.importedCount || 0 : d.contactsCount || 0,
              emailsCount: isCsv ? d.emailsCount || 0 : 0,
              phonesCount: isCsv ? d.phonesCount || 0 : 0,
              bucketName: d?.bucketName || null,
              linkUrl: payload.linkUrl || null,
              contactIds: d?.contactIds || [],
              timestamp: Date.now(),
            });
          }
          return;
        }
        case NotificationCategory.account:
        case NotificationCategory.meeting:
        case NotificationCategory.task:
        default:
          break;
      }

      if (payload.data?.eventType === "attendee_rsvp" && payload.data?.eventId) {
        window.dispatchEvent(
          new CustomEvent("rsvp-updated", {
            detail: { meetingId: payload.data.eventId },
          })
        );
        return;
      }

      commit(PUSH_NOTIFICATION_POPUP, payload);
    },

    [CLOSE_NOTIFICATION_POPUP_ACTION]: ({ commit, state }) => {
      commit(CLOSE_NOTIFICATION_POPUP);
      if (state.popupQueue.length) commit(NEXT_NOTIFICATION_POPUP);
    },
  },
};

export default notification;
